import { useState } from "react";
import { ChevronDown, ChevronRight, Undo2 } from "lucide-react";
import { KIND_SHORT, PLATFORM_LABEL } from "../preview/meta";

const BADGE = {
  applied: "border-emerald-200 bg-emerald-50 text-emerald-700", skipped: "border-slate-200 bg-slate-50 text-slate-600",
  failed: "border-red-200 bg-red-50 text-red-700", queued: "border-sky-200 bg-sky-50 text-sky-700",
};

const Badge = ({ s, children }) => <span className={`rounded border px-1.5 py-px text-[10px] font-semibold ${BADGE[s] || BADGE.skipped}`}>{children || s}</span>;

function Items({ items }) {
  const [open, setOpen] = useState(false);
  if (!items?.length) return null;
  const bad = items.filter((i) => i.status === "failed").length;
  return (
    <div className="mt-1">
      <button type="button" onClick={() => setOpen(!open)} className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-800" data-testid="bsh-items-toggle">
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}{items.length} item{items.length === 1 ? "" : "s"}{bad ? `, ${bad} failed` : ""}</button>
      {open && <ul className="mt-1 space-y-0.5 border-l border-slate-200 pl-2">{items.map((i) => (
        <li key={i.reference} className="text-[11px]"><Badge s={i.status} /> <span className="font-mono text-slate-700">{i.reference}</span> <span className="text-slate-500">{i.message}</span></li>))}</ul>}
    </div>
  );
}

function UndoCell({ r, canUndo, onUndo }) {
  if (r.undo) return <div data-testid={`bsh-undo-state-${r.seq}`}><Badge s={r.undo.status}>{r.undo.status === "applied" ? "undone" : `undo ${r.undo.status}`}</Badge>
    {r.undo.message && <div className="mt-0.5 text-[11px] text-slate-500">{r.undo.message}</div>}</div>;
  if (!canUndo || r.status !== "applied" || !r.undo_info) return null;
  return (
    <button type="button" onClick={() => onUndo([{ apply_id: r.apply_id, seq: r.seq }])} data-testid={`bsh-undo-${r.seq}`}
            className="inline-flex items-center gap-1 rounded border border-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-600 transition-colors hover:border-violet-300 hover:bg-violet-50 hover:text-violet-700">
      <Undo2 className="h-3 w-3" /> Undo</button>
  );
}

export function DetailRows({ rows, isUndoJob, canUndo, onUndo }) {
  return (
    <table className="w-full text-xs" data-testid="bsh-rows-table">
      <thead className="sticky top-0 z-10 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500"><tr>
        <th className="w-28 px-4 py-2.5 text-left">Change</th><th className="w-[22%] px-3 py-2.5 text-left">Domain</th><th className="w-[20%] px-3 py-2.5 text-left">Item</th>
        <th className="px-3 py-2.5 text-left">Result</th>{!isUndoJob && <th className="w-[18%] px-4 py-2.5 text-left">Undo</th>}</tr></thead>
      <tbody>{rows.map((r) => (
        <tr key={r.seq} className="border-t border-slate-100 align-top hover:bg-slate-50/70" data-testid={`bsh-detail-row-${r.seq}`}>
          <td className="px-4 py-2.5 font-medium text-slate-700">{KIND_SHORT[r.kind] || r.kind}{r.platform && <div className="text-[10px] font-normal text-slate-400">{PLATFORM_LABEL[r.platform]}</div>}</td>
          <td className="px-3 py-2.5 text-slate-800">{r.domain || "—"}{r.territory && <div className="font-mono text-[10px] text-slate-400">{r.territory}</div>}</td>
          <td className="px-3 py-2.5 font-mono text-[11px] text-slate-700"><span className="break-all">{r.reference || r.customer || "—"}</span>
            {r.product_name && <div className="font-sans text-[10px] text-slate-500">{r.from_product_name && r.kind === "move" ? `${r.from_product_name} → ` : ""}{r.product_name}</div>}</td>
          <td className="px-3 py-2.5"><Badge s={r.status} /> <span className="text-[11px] text-slate-600" data-testid={`bsh-result-${r.seq}`}>{r.message}</span><Items items={r.items} /></td>
          {!isUndoJob && <td className="px-4 py-2.5"><UndoCell r={r} canUndo={canUndo} onUndo={onUndo} /></td>}
        </tr>))}
      </tbody>
    </table>
  );
}
